import { ILogger } from '@trimble/logging';
import { Vector2, Web3DViewer } from '@technology/web3d';
import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import { type CadSceneProjection } from './cad-scene-projection';
import { Accessor, createEffect } from 'solid-js';
import { FeaturesServiceCapabilityProvider } from '@trimble/web3d-providers';
import {
  featureTileFetcher,
  FeaturesServiceOptions,
} from '@trimble/web3d-plugin-features-service';
import {
  FEATURE_TILE_MIN_ZOOM_LEVEL,
  FEATURE_TILE_MAX_ZOOM_LEVEL,
  FEATURE_TILE_IMAGE_SIZE,
  modelIdForPointCloud,
  WEB_MERCATOR_PROJECTION,
  ROOT_ZOOM_LEVEL,
} from './feature-tile-config';
import type { PointCloudRoot } from './feature-extraction-types';
import { createFeatureIngestBuffer } from './feature-ingest-buffer';

export interface CreateFeatureServiceModelLoaderParams {
  readonly viewer: Web3DViewer;
  readonly context: FeatureExtractionContextType;
  readonly logger: ILogger;
  readonly csib: string;
  readonly geoDataFiles: Map<string, ArrayBuffer>;
  readonly pointCloudRoots: Accessor<PointCloudRoot[]>;
  readonly projection: Pick<
    CadSceneProjection,
    'converterSettled' | 'converter' | 'ensureConverter'
  >;
  elevationSampler(x: number, y: number): number | undefined;
}

export interface FeatureServiceModelLoader {
  dispose(): void;
}

export function createFeatureServiceModelLoader(
  params: CreateFeatureServiceModelLoaderParams
): FeatureServiceModelLoader {
  // loadModel is async; dispose is sync — ignore loads that finish after teardown.
  let disposed = false;
  const {
    viewer,
    context,
    logger,
    csib,
    geoDataFiles,
    pointCloudRoots,
    projection,
    elevationSampler,
  } = params;

  const featuresProvider = new FeaturesServiceCapabilityProvider(viewer, {
    csib,
    geoDataFiles,
  });
  const featureIngestBuffer = createFeatureIngestBuffer({
    context,
    isDisposed: () => disposed,
  });

  const loadedModelIds = new Set<string>();
  const loadGenerations = new Map<string, number>();
  const modelUnsubscribers = new Map<string, () => void>();
  const advanceLoadGeneration = (modelId: string): number => {
    const generation = (loadGenerations.get(modelId) ?? 0) + 1;
    loadGenerations.set(modelId, generation);
    return generation;
  };
  const syncLayerVisibilityToModel = (modelId: string): void => {
    context.editingState.layers.forEach((layer) => {
      featuresProvider.setLayerVisible(
        modelId,
        layer.id,
        context.isLayerVisible(layer.id)
      );
    });
  };
  const isCurrentLoad = (modelId: string, generation: number): boolean =>
    loadedModelIds.has(modelId) && loadGenerations.get(modelId) === generation;
  const syncLayerVisibilityToLoadedModels = (): void => {
    if (disposed) {
      return;
    }
    loadedModelIds.forEach(syncLayerVisibilityToModel);
  };

  createEffect(() => {
    const layers = context.editingState.layers;
    for (const layer of layers) {
      context.isLayerVisible(layer.id);
    }
    syncLayerVisibilityToLoadedModels();
  });

  const loadModelForRoot = async (
    root: PointCloudRoot,
    modelId: string,
    generation: number
  ): Promise<void> => {
    const scope = context.scope();
    const layerIds = context.editingState.layers.map((layer) => layer.id);
    if (!scope || layerIds.length === 0) {
      logger.warn('Skipping feature load; missing project scope or layers', {
        hasScope: Boolean(scope),
        layerCount: layerIds.length,
      });
      if (isCurrentLoad(modelId, generation)) {
        loadedModelIds.delete(modelId);
      }
      return;
    }

    try {
      await featuresProvider.whenReady();
      if (!isCurrentLoad(modelId, generation)) {
        return;
      }
      await projection.ensureConverter();
      if (!isCurrentLoad(modelId, generation)) {
        return;
      }

      let rootCoordinate = new Vector2(0, 0);
      try {
        // Plugin proj4 cannot resolve TGL scene projection.
        const lonLat = projection
          .converter()
          ?.sceneToLonLat(root.center.x, root.center.y);
        if (!lonLat) {
          throw new Error('scene->lon/lat conversion unavailable');
        }
        const tile = featuresProvider.getTileCoordsFromLatLon(
          lonLat.lat,
          lonLat.lon,
          ROOT_ZOOM_LEVEL
        );
        rootCoordinate = new Vector2(tile.x, tile.y);
      } catch (error) {
        logger.warn(
          'Failed to derive feature tile root coordinate; defaulting to origin',
          { pointCloudId: root.id },
          error instanceof Error ? error : new Error(String(error))
        );
      }

      const options: FeaturesServiceOptions = {
        modelId,
        debug: false,
        tileAvailableFn: () => true,
        projection: WEB_MERCATOR_PROJECTION,
        elevationSampler,
        tileOptions: {
          imageSize: FEATURE_TILE_IMAGE_SIZE,
          maxZoomLevel: FEATURE_TILE_MAX_ZOOM_LEVEL,
          minZoomLevel: FEATURE_TILE_MIN_ZOOM_LEVEL,
          rootCoordinate,
          rootZoomLevel: ROOT_ZOOM_LEVEL,
        },
        tileFetcher: {
          fetcher: featureTileFetcher({
            projectId: scope.projectId,
            layerIds,
            authToken: () => context.authToken() ?? null,
            fetchMode: 'per-layer',
            timestamp: () => context.tileCacheToken(),
          }),
        },
      };

      const featuresModel = await featuresProvider.loadModel(options);
      if (!featuresModel) {
        if (isCurrentLoad(modelId, generation)) {
          loadedModelIds.delete(modelId);
        }
        return;
      }
      if (!isCurrentLoad(modelId, generation)) {
        if (disposed || !loadedModelIds.has(modelId)) {
          await featuresProvider
            .unloadModel(featuresModel.modelId)
            .catch(
              (error) =>
                void logger.warn(
                  'Failed to unload stale feature service model',
                  { modelId: featuresModel.modelId },
                  error instanceof Error ? error : new Error(String(error))
                )
            );
        }
        return;
      }
      modelUnsubscribers.set(
        modelId,
        featuresModel.addFeatureEventListener(
          featureIngestBuffer.handleFeatureEvent
        )
      );
      syncLayerVisibilityToModel(modelId);
      logger.info('Loaded feature service model', {
        modelId,
        pointCloudId: root.id,
        rootX: rootCoordinate.x,
        rootY: rootCoordinate.y,
      });
    } catch (error) {
      if (isCurrentLoad(modelId, generation)) {
        loadedModelIds.delete(modelId);
      }
      logger.error(
        'Failed to load feature service model',
        { modelId, pointCloudId: root.id },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  };
  const unloadModel = (modelId: string): void => {
    advanceLoadGeneration(modelId);
    loadedModelIds.delete(modelId);
    modelUnsubscribers.get(modelId)?.();
    modelUnsubscribers.delete(modelId);
    void featuresProvider.unloadModel(modelId).catch((error) => {
      logger.warn(
        'Failed to unload feature service model',
        { modelId },
        error instanceof Error ? error : new Error(String(error))
      );
    });
  };

  createEffect(() => {
    const roots = pointCloudRoots().filter((root) => root.csib === csib);
    // Loads without a converter too — tiles are still useful unprojected.
    const settled = projection.converterSettled();
    const hasLayers = context.editingState.layers.length > 0;
    const hasScope = Boolean(context.scope());
    if (disposed || !settled || !hasLayers || !hasScope) {
      return;
    }
    const targetModelIds = new Set(
      roots.map((root) => modelIdForPointCloud(root.id))
    );
    for (const modelId of Array.from(loadedModelIds)) {
      if (!targetModelIds.has(modelId)) {
        unloadModel(modelId);
      }
    }
    for (const root of roots) {
      const modelId = modelIdForPointCloud(root.id);
      if (loadedModelIds.has(modelId)) {
        continue;
      }
      loadedModelIds.add(modelId);
      const generation = advanceLoadGeneration(modelId);
      void loadModelForRoot(root, modelId, generation);
    }
  });

  return {
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
      modelUnsubscribers.forEach((unsubscribe) => unsubscribe());
      modelUnsubscribers.clear();
      loadedModelIds.forEach(advanceLoadGeneration);
      loadedModelIds.clear();
      void featuresProvider.cleanup().catch((error) => {
        logger.warn(
          'Failed to unload feature service models on dispose',
          undefined,
          error instanceof Error ? error : new Error(String(error))
        );
      });
    },
  };
}
